import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Shield, Eye } from "lucide-react";

export default function UsersSection() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!email.trim()) return toast.error("E-posta girin");
    if (password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    setSaving(true);
    try {
      await api.post("/auth/users", { email: email.trim(), name: name.trim(), password, role });
      toast.success("Kullanıcı eklendi");
      setEmail("");
      setName("");
      setPassword("");
      setRole("viewer");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Kullanıcıları ve yetkilerini yönetin. <span className="font-medium">Yönetici</span> her şeyi yapabilir; <span className="font-medium">İzleyici</span> yalnızca görüntüler.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-new-user-btn" className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white">
              <UserPlus className="h-4 w-4" /> Yeni Kullanıcı
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Yeni Kullanıcı Ekle</DialogTitle>
              <DialogDescription>Giriş bilgilerini ve yetkiyi belirleyin.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Ad (opsiyonel)</Label>
                <Input data-testid="user-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Ahmet Yılmaz" />
              </div>
              <div className="space-y-1.5">
                <Label>E-posta</Label>
                <Input data-testid="user-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@eposta.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Şifre</Label>
                <Input data-testid="user-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="En az 6 karakter" />
              </div>
              <div className="space-y-1.5">
                <Label>Yetki</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Yönetici (Admin)</SelectItem>
                    <SelectItem value="viewer">İzleyici</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-user-btn" onClick={create} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto">
                {saving ? "Kaydediliyor..." : "Kullanıcı Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`user-card-${u.id}`}
            className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-start justify-between"
          >
            <div>
              <p className="font-semibold text-slate-900">{u.name || u.email.split("@")[0]}</p>
              <p className="text-xs text-slate-400 mt-0.5">{u.email}</p>
              <Badge
                variant="outline"
                className={`mt-2 gap-1 ${
                  u.role === "admin"
                    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                }`}
              >
                {u.role === "admin" ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {u.role === "admin" ? "Yönetici" : "İzleyici"}
              </Badge>
            </div>
            {me?.id !== u.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid={`delete-user-${u.id}`}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50 -mr-1 -mt-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Kullanıcıyı sil?</AlertDialogTitle>
                    <AlertDialogDescription>
                      "{u.email}" hesabı kalıcı olarak silinecek.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`confirm-delete-user-${u.id}`}
                      onClick={() => remove(u.id)}
                      className="bg-rose-600 hover:bg-rose-700"
                    >
                      Sil
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
